/**
 * Shared MUI sx style objects used across multiple components.
 * Centralised here to avoid repetition and ensure consistency.
 */
import { COLORS } from 'pvs-design-system';

/** Purple shimmer skeleton used on page-level loading states. */
export const SHIMMER_PURPLE_SX = {
  background: 'linear-gradient(90deg, #ede7f6 25%, #f3eeff 50%, #ede7f6 75%)',
  backgroundSize: '200% 100%',
  animation: 'shimmer 1.4s infinite',
  borderRadius: 1,
} as const;

/** Gray shimmer skeleton used in list/sidebar loading states. */
export const SHIMMER_GRAY_SX = {
  background: 'linear-gradient(90deg, #f0f0f0 25%, #fafafa 50%, #f0f0f0 75%)',
  backgroundSize: '200% 100%',
  animation: 'shimmer 1.4s infinite',
  borderRadius: 1,
} as const;

/**
 * Neutral shimmer used by the browse/search content skeletons.
 * Kept at the exact gradient the pages rendered inline (#f5f5f5 → #fafafa)
 * so the loading state is visually identical after centralisation.
 */
export const SHIMMER_CONTENT_SX = {
  background: 'linear-gradient(90deg, #f5f5f5 25%, #fafafa 50%, #f5f5f5 75%)',
  backgroundSize: '200% 100%',
  animation: 'shimmer 1.4s infinite',
} as const;

/** Clickable table row: pointer cursor + beige hover on its cells. */
export const LIBRARY_ROW_SX = {
  cursor: 'pointer',
  // TableCell paints its own opaque white background, so the row's own
  // hover bgcolor is hidden underneath it — target the cells directly.
  '&:hover .MuiTableCell-root': { backgroundColor: COLORS.beige[200] },
  // Keyboard focus: suppress the ring ONLY for mouse focus (:focus:not(:focus-visible)),
  // never for plain :focus — so keyboard users always keep a visible ring, including in
  // browsers without :focus-visible support (there the whole selector is ignored and the
  // default outline shows as a fallback). Show a clear purple ring on keyboard focus.
  '&:focus:not(:focus-visible)': { outline: 'none' },
  '&:focus-visible': {
    outline: `2px solid ${COLORS.purple[700]}`,
    outlineOffset: '-2px',
  },
  '&:focus-visible .MuiTableCell-root': { backgroundColor: COLORS.beige[200] },
} as const;

/** Folder/resource card in the grid view. */
export const LIBRARY_GRID_CARD_SX = {
  display: 'flex',
  flexDirection: 'column' as const,
  p: 1.5,
  border: `1px solid ${COLORS.gray[200]}`,
  borderRadius: 2,
  background: COLORS.white,
  cursor: 'pointer',
  textAlign: 'left' as const,
  width: '100%',
  transition: 'border-color 0.15s, box-shadow 0.15s',
  '&:hover': { borderColor: COLORS.purple[700], boxShadow: '0 2px 10px rgba(106,27,131,0.08)' },
  '&:hover .info-reveal, &:focus-within .info-reveal': { opacity: 1 },
  // Keyboard focus: suppress the ring ONLY for mouse focus (:focus:not(:focus-visible)),
  // never for plain :focus — so keyboard users always keep a visible ring, including in
  // browsers without :focus-visible support (there the whole selector is ignored and the
  // default outline shows as a fallback). Show a clear purple ring on keyboard focus.
  '&:focus:not(:focus-visible)': { outline: 'none' },
  '&:focus-visible': {
    outline: `2px solid ${COLORS.purple[700]}`,
    outlineOffset: '2px',
    borderColor: COLORS.purple[700],
  },
};

/** Recently-viewed card on the IndexPage hero (secondary-button-like hover/focus). */
export const RECENT_CARD_SX = {
  display: 'flex',
  flexDirection: 'column' as const,
  alignItems: 'flex-start',
  gap: 0.375,
  p: 1.25,
  border: `1px solid ${COLORS.gray[200]}`,
  borderRadius: 2,
  background: COLORS.white,
  cursor: 'pointer',
  textAlign: 'left' as const,
  width: '100%',
  transition: 'border-color 0.15s, box-shadow 0.15s, background-color 0.15s',
  '&:hover': {
    borderColor: COLORS.purple[800],
    backgroundColor: COLORS.hotPink[100],
    boxShadow: `0 0 0 0.5px ${COLORS.purple[800]}`,
  },
  '&:focus': {
    outline: 'none',
    borderColor: COLORS.purple[800],
    backgroundColor: COLORS.hotPink[100],
    boxShadow: `0 0 0 0.5px ${COLORS.purple[800]}, 0 0 0 1.5px ${COLORS.white}, 0 0 0 2.5px ${COLORS.purple[800]}`,
  },
};
